// components/admin/AdminAjustarProgreso.js
// Componente para que el admin ajuste manualmente el progreso de usuarios
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const API = '/backend/api/admin/adjust-progress.php';

const getHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const Container = styled.div`padding: 1rem 0;`;
const Card = styled.div`background: white; border-radius: 10px; padding: 1.5rem; box-shadow: 0 1px 4px rgba(0,0,0,0.08); margin-bottom: 1rem;`;
const Title = styled.h3`margin: 0 0 1rem; color: #2b4361;`;
const Row = styled.div`display: flex; gap: 1rem; align-items: center; flex-wrap: wrap; margin-bottom: 1rem;`;
const Select = styled.select`padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.9rem;`;
const Input = styled.input`padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.9rem; width: 280px;`;
const Btn = styled.button`
  padding: 0.5rem 1rem; border: none; border-radius: 6px; cursor: pointer; font-size: 0.85rem; font-weight: 600;
  background: ${p => p.$danger ? '#ef4444' : p.$secondary ? '#6b7280' : '#0891B2'}; 
  color: white; 
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const Badge = styled.span`
  display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;
  background: ${p => p.$completed ? '#dcfce7' : '#fef3c7'}; 
  color: ${p => p.$completed ? '#166534' : '#92400e'};
`;
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 0.85rem;
  th, td { padding: 0.5rem; text-align: left; border-bottom: 1px solid #e5e7eb; }
  th { background: #f9fafb; font-weight: 600; color: #374151; }
  tr:hover { background: #f0fdfa; }
`;
const Msg = styled.div`padding: 0.75rem; border-radius: 6px; margin: 0.5rem 0; font-size: 0.9rem;
  background: ${p => p.$error ? '#fef2f2' : '#f0fdf4'}; color: ${p => p.$error ? '#991b1b' : '#166534'};
`;
const SearchResults = styled.div`background: white; border: 1px solid #d1d5db; border-radius: 6px; max-height: 200px; overflow-y: auto; position: absolute; z-index: 10; width: 280px;`;
const SearchItem = styled.div`padding: 0.5rem 0.75rem; cursor: pointer; font-size: 0.85rem; border-bottom: 1px solid #f3f4f6;
  &:hover { background: #f0fdfa; }
  small { color: #6b7280; }
`;
const SearchWrap = styled.div`position: relative;`;

const AdminAjustarProgreso = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cargar documentos con mentor
  useEffect(() => {
    axios.get(`${API}?action=documents`, getHeaders())
      .then(r => setDocuments(r.data))
      .catch(() => {});
  }, []);

  // Buscar usuarios
  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(() => {
      axios.get(`${API}?action=search_users&q=${encodeURIComponent(searchQuery)}&document_id=${selectedDoc}`, getHeaders())
        .then(r => setSearchResults(r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedDoc]);

  // Cargar progreso del usuario seleccionado
  const loadProgress = useCallback(async (userId, docId) => {
    if (!userId || !docId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}?action=user_progress&user_id=${userId}&document_id=${docId}`, getHeaders());
      setUserProgress(r.data);
      setSelectedVideos([]);
    } catch (e) {
      setMessage({ text: 'Error cargando progreso', error: true });
    }
    setLoading(false);
  }, []);

  const selectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery(user.email);
    setSearchResults([]);
    if (selectedDoc) loadProgress(user.id, selectedDoc);
  };

  useEffect(() => {
    if (selectedUser && selectedDoc) loadProgress(selectedUser.id, selectedDoc);
  }, [selectedDoc, selectedUser, loadProgress]);

  const toggleVideo = (videoId) => {
    setSelectedVideos(prev => 
      prev.includes(videoId) ? prev.filter(v => v !== videoId) : [...prev, videoId]
    );
  };

  const selectAllIncomplete = () => {
    if (!userProgress?.videos) return;
    const incomplete = userProgress.videos
      .filter(v => !v.completado && !(v.duracion_segundos > 0 && v.timestamp_maximo >= v.duracion_segundos * 0.9))
      .map(v => v.id);
    setSelectedVideos(incomplete);
  };

  const markCompleted = async () => {
    if (!selectedVideos.length) return;
    setLoading(true);
    try {
      const r = await axios.post(API, {
        action: 'mark_videos_completed',
        user_id: selectedUser.id,
        document_id: selectedDoc,
        video_ids: selectedVideos
      }, getHeaders());
      setMessage({ text: r.data.message });
      loadProgress(selectedUser.id, selectedDoc);
    } catch (e) {
      setMessage({ text: 'Error actualizando', error: true });
    }
    setLoading(false);
  };

  const markModule = async (moduloNum) => {
    setLoading(true);
    try {
      const r = await axios.post(API, {
        action: 'mark_module_completed',
        user_id: selectedUser.id,
        document_id: selectedDoc,
        modulo_numero: moduloNum
      }, getHeaders());
      setMessage({ text: r.data.message });
      loadProgress(selectedUser.id, selectedDoc);
    } catch (e) {
      setMessage({ text: 'Error actualizando módulo', error: true });
    }
    setLoading(false);
  };

  const unmarkVideo = async (videoId) => {
    setLoading(true);
    try {
      await axios.post(API, {
        action: 'unmark_video',
        user_id: selectedUser.id,
        video_id: videoId,
        document_id: selectedDoc
      }, getHeaders());
      setMessage({ text: 'Video desmarcado' });
      loadProgress(selectedUser.id, selectedDoc);
    } catch (e) {
      setMessage({ text: 'Error desmarcando', error: true });
    }
    setLoading(false);
  };

  const resetEvaluation = async (evalId) => {
    if (!window.confirm('¿Eliminar esta evaluación? El usuario podrá reintentar.')) return;
    try {
      const r = await axios.post(API, { action: 'reset_evaluation', evaluation_id: evalId }, getHeaders());
      setMessage({ text: r.data.message });
      loadProgress(selectedUser.id, selectedDoc);
    } catch (e) {
      setMessage({ text: 'Error eliminando evaluación', error: true });
    }
  };

  // Agrupar videos por módulo
  const videosByModule = {};
  if (userProgress?.videos) {
    userProgress.videos.forEach(v => {
      const mod = v.modulo_numero || 1;
      if (!videosByModule[mod]) videosByModule[mod] = [];
      videosByModule[mod].push(v);
    });
  }

  return (
    <Container>
      <Card>
        <Title>Ajustar Progreso de Usuario</Title>
        <Row>
          <Select value={selectedDoc} onChange={e => setSelectedDoc(e.target.value)}>
            <option value="">Seleccionar documento...</option>
            {documents.map(d => (
              <option key={d.id} value={d.id}>{d.titulo} ({d.total_videos} videos)</option>
            ))}
          </Select>
          <SearchWrap>
            <Input 
              placeholder="Buscar usuario por email o nombre..." 
              value={searchQuery} 
              onChange={e => { setSearchQuery(e.target.value); setSelectedUser(null); setUserProgress(null); }}
            />
            {searchResults.length > 0 && (
              <SearchResults>
                {searchResults.map(u => (
                  <SearchItem key={u.id} onClick={() => selectUser(u)}>
                    <strong>{u.nombre}</strong><br/>
                    <small>{u.email} {u.progreso_estado ? `— ${u.progreso_estado}` : ''}</small>
                  </SearchItem>
                ))}
              </SearchResults>
            )}
          </SearchWrap>
        </Row>
        {message && <Msg $error={message.error}>{message.text}</Msg>}
      </Card>

      {loading && <Card>Cargando...</Card>}

      {userProgress && !loading && (
        <>
          <Card>
            <Title>
              {userProgress.usuario.nombre} — {userProgress.documento?.titulo}
            </Title>
            <Row>
              <Badge $completed={userProgress.resumen.porcentaje === 100}>
                {userProgress.resumen.completados}/{userProgress.resumen.total_videos} videos — {userProgress.resumen.porcentaje}%
              </Badge>
              {userProgress.progreso_general && (
                <span style={{fontSize: '0.85rem', color: '#6b7280'}}>
                  Estado: {userProgress.progreso_general.estado} | 
                  Posición: M{userProgress.progreso_general.modulo_actual} L{userProgress.progreso_general.leccion_actual}
                </span>
              )}
            </Row>
          </Card>

          {Object.entries(videosByModule).map(([modNum, videos]) => {
            const allComplete = videos.every(v => v.completado == 1 || (v.duracion_segundos > 0 && v.timestamp_maximo >= v.duracion_segundos * 0.9));
            return (
              <Card key={modNum}>
                <Row style={{justifyContent: 'space-between'}}>
                  <Title style={{margin: 0}}>Módulo {modNum} {allComplete && '✓'}</Title>
                  {!allComplete && (
                    <Btn onClick={() => markModule(parseInt(modNum))} $secondary>
                      Completar módulo entero
                    </Btn>
                  )}
                </Row>
                <Table>
                  <thead>
                    <tr>
                      <th style={{width: 30}}>
                        <input type="checkbox" onChange={selectAllIncomplete} title="Seleccionar incompletos" />
                      </th>
                      <th>Lección</th>
                      <th>Video</th>
                      <th>Duración</th>
                      <th>Progreso</th>
                      <th>Estado</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {videos.map(v => {
                      const isComplete = v.completado == 1 || (v.duracion_segundos > 0 && v.timestamp_maximo >= v.duracion_segundos * 0.9);
                      const pct = v.duracion_segundos > 0 ? Math.round((v.timestamp_maximo || 0) / v.duracion_segundos * 100) : 0;
                      return (
                        <tr key={v.id}>
                          <td>
                            {!isComplete && (
                              <input type="checkbox" checked={selectedVideos.includes(v.id)} onChange={() => toggleVideo(v.id)} />
                            )}
                          </td>
                          <td>{v.leccion_numero}</td>
                          <td style={{maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {v.titulo_completo}
                          </td>
                          <td>{v.duracion_segundos ? `${Math.floor(v.duracion_segundos/60)}m` : '—'}</td>
                          <td>{v.timestamp_maximo ? `${pct}%` : '—'}</td>
                          <td><Badge $completed={isComplete}>{isComplete ? 'Completado' : 'Pendiente'}</Badge></td>
                          <td>
                            {isComplete ? (
                              <Btn $danger onClick={() => unmarkVideo(v.id)} style={{padding: '2px 8px', fontSize: '0.75rem'}}>
                                Desmarcar
                              </Btn>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                {selectedVideos.length > 0 && (
                  <Row style={{marginTop: '0.75rem'}}>
                    <Btn onClick={markCompleted}>
                      Marcar {selectedVideos.length} video(s) como completados
                    </Btn>
                  </Row>
                )}
              </Card>
            );
          })}

          {/* Evaluaciones */}
          {userProgress.evaluaciones && userProgress.evaluaciones.length > 0 && (
            <Card>
              <Title>Evaluaciones</Title>
              <Table>
                <thead>
                  <tr>
                    <th>Intento</th>
                    <th>Preguntas</th>
                    <th>Correctas</th>
                    <th>Porcentaje</th>
                    <th>Aprobado</th>
                    <th>Fecha</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {userProgress.evaluaciones.map(ev => (
                    <tr key={ev.id}>
                      <td>#{ev.numero_intento}</td>
                      <td>{ev.total_preguntas}</td>
                      <td>{ev.preguntas_correctas}</td>
                      <td>{ev.porcentaje_obtenido}%</td>
                      <td><Badge $completed={ev.aprobado == 1}>{ev.aprobado == 1 ? 'Sí' : 'No'}</Badge></td>
                      <td>{ev.fecha_inicio ? new Date(ev.fecha_inicio).toLocaleDateString() : '—'}</td>
                      <td>
                        <Btn $danger onClick={() => resetEvaluation(ev.id)} style={{padding: '2px 8px', fontSize: '0.75rem'}}>
                          Eliminar
                        </Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
        </>
      )}
    </Container>
  );
};

export default AdminAjustarProgreso;
